import React, { useState, useCallback, useEffect } from 'react';
import {
  useThemes,
  useCreateTheme,
  useUpdateTheme,
  useDeleteTheme,
  useGenerateTheme,
  ThemeOutput,
  ThemeTokens,
  GeneratedTheme,
} from '../helpers/useThemesApi';
import { useGeneratedImages, useGenerateImage, ImageOutput } from '../helpers/useImagesApi';
import { useSiteTheme } from '../hooks/useSiteTheme';
import { useSessionFiles, useCreateFile, useUpdateFile } from '../helpers/useFilesApi';
import { Button } from './Button';
import { Badge } from './Badge';
import { Skeleton } from './Skeleton';
import { Spinner } from './Spinner';
import { Textarea } from './Textarea';
import { Input } from './Input';
import {
  Palette,
  Sparkles,
  Image,
  Plus,
  Trash2,
  Check,
  Copy,
  Download,
  RefreshCw,
  Eye,
  FileCode,
  CheckCircle2,
  Wand2,
  Loader2,
  ImagePlus,
  FolderOpen,
  Monitor,
  RotateCcw,
  Dices,
  Moon,
  Sun,
  Waves,
  Trees,
  Flame,
  Gem,
  Zap,
  Building2,
  Heart,
  Coffee,
  Gamepad2,
  Shield,
  Undo2,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './Dialog';
import { Tooltip, TooltipContent, TooltipTrigger } from './Tooltip';
import { Checkbox } from './Checkbox';
import styles from './ThemesPanel.module.css';
import { normalizeImageDataUrl } from '../helpers/normalizeImageDataUrl';

interface ThemesPanelProps {
  sessionId: string;
  isOwner?: boolean;
  hideProjectScope?: boolean;
}

type TabType = 'themes' | 'generate' | 'images';

type ThemeSuggestion = {
  id: string;
  label: string;
  prompt: string;
};

type ThemeIconComponent = React.ComponentType<{ size?: number; className?: string }>;

// Color token metadata: category, label, description for tooltip
const COLOR_META: Record<string, { cat: string; label: string; desc: string }> = {
  // Core brand
  primary: { cat: 'Brand', label: 'Primary', desc: 'Buttons, links, active states' },
  primaryForeground: { cat: 'Brand', label: 'Primary Text', desc: 'Text on primary-colored elements' },
  secondary: { cat: 'Brand', label: 'Secondary', desc: 'Secondary buttons, badges, tags' },
  secondaryForeground: { cat: 'Brand', label: 'Secondary Text', desc: 'Text on secondary elements' },
  accent: { cat: 'Brand', label: 'Accent', desc: 'Highlights, focus rings, accents' },
  accentForeground: { cat: 'Brand', label: 'Accent Text', desc: 'Text on accent elements' },
  // Backgrounds
  background: { cat: 'Backgrounds', label: 'Background', desc: 'Page background' },
  foreground: { cat: 'Backgrounds', label: 'Foreground', desc: 'Default text color on page' },
  surface: { cat: 'Backgrounds', label: 'Surface', desc: 'Panels, sections, content areas' },
  surfaceForeground: { cat: 'Backgrounds', label: 'Surface Text', desc: 'Text on surface panels' },
  card: { cat: 'Backgrounds', label: 'Card', desc: 'Card backgrounds, dialogs' },
  cardForeground: { cat: 'Backgrounds', label: 'Card Text', desc: 'Text inside cards' },
  sidebar: { cat: 'Backgrounds', label: 'Sidebar', desc: 'Sidebar/navigation background' },
  sidebarForeground: { cat: 'Backgrounds', label: 'Sidebar Text', desc: 'Text in sidebar' },
  popup: { cat: 'Backgrounds', label: 'Popup', desc: 'Dropdown menus, popovers, tooltips' },
  popupForeground: { cat: 'Backgrounds', label: 'Popup Text', desc: 'Text in popups' },
  muted: { cat: 'Backgrounds', label: 'Muted', desc: 'Disabled backgrounds, subtle fills' },
  mutedForeground: { cat: 'Backgrounds', label: 'Muted Text', desc: 'Placeholder text, captions, hints' },
  // Legacy text (mapped when granular tokens missing)
  text: { cat: 'Backgrounds', label: 'Text', desc: 'Default body text color' },
  textMuted: { cat: 'Backgrounds', label: 'Text Muted', desc: 'Subtle text, hints, placeholders' },
  // Borders
  border: { cat: 'Borders & Input', label: 'Border', desc: 'Card borders, dividers, outlines' },
  mutedBorder: { cat: 'Borders & Input', label: 'Muted Border', desc: 'Subtle dividers, table lines' },
  input: { cat: 'Borders & Input', label: 'Input', desc: 'Input field borders' },
  ring: { cat: 'Borders & Input', label: 'Focus Ring', desc: 'Focus outline around interactive elements' },
  // Status
  error: { cat: 'Status', label: 'Error', desc: 'Error messages, destructive actions' },
  errorForeground: { cat: 'Status', label: 'Error Text', desc: 'Text on error backgrounds' },
  success: { cat: 'Status', label: 'Success', desc: 'Success states, confirmations' },
  successForeground: { cat: 'Status', label: 'Success Text', desc: 'Text on success backgrounds' },
  warning: { cat: 'Status', label: 'Warning', desc: 'Warning messages, caution states' },
  warningForeground: { cat: 'Status', label: 'Warning Text', desc: 'Text on warning backgrounds' },
  info: { cat: 'Status', label: 'Info', desc: 'Informational banners, tooltips' },
  infoForeground: { cat: 'Status', label: 'Info Text', desc: 'Text on info backgrounds' },
  // Charts
  chart1: { cat: 'Charts', label: 'Chart 1', desc: 'First data series color' },
  chart2: { cat: 'Charts', label: 'Chart 2', desc: 'Second data series color' },
  chart3: { cat: 'Charts', label: 'Chart 3', desc: 'Third data series color' },
  chart4: { cat: 'Charts', label: 'Chart 4', desc: 'Fourth data series color' },
  chart5: { cat: 'Charts', label: 'Chart 5', desc: 'Fifth data series color' },
};

const COLOR_CATEGORIES = ['Brand', 'Backgrounds', 'Borders & Input', 'Status', 'Charts'];

/** Dynamically load a Google Font by injecting a <link> tag (idempotent) */
const _loadedFonts = new Set<string>();
function loadGoogleFont(label: string) {
  // Skip system/web-safe fonts that don't need loading
  const SYSTEM_FONTS = ['System UI', 'Arial', 'Arial Black', 'Helvetica Neue', 'Verdana', 'Tahoma',
    'Trebuchet MS', 'Gill Sans', 'Segoe UI', 'Calibri', 'Times New Roman', 'Georgia', 'Garamond',
    'Palatino', 'Baskerville', 'Consolas', 'Menlo', 'Courier New', 'Lucida Console',
    'Brush Script MT', 'Bradley Hand', 'Lucida Handwriting'];
  if (SYSTEM_FONTS.includes(label) || _loadedFonts.has(label)) return;
  _loadedFonts.add(label);
  const family = label.replace(/ /g, '+');
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${family}:ital,wght@0,200..900;1,200..900&display=swap`;
  document.head.appendChild(link);
}

const FONT_OPTIONS: { label: string; value: string; category: string }[] = [
  // ── Sans-serif ──
  { label: 'System UI', value: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif', category: 'Sans-serif' },
  { label: 'Inter', value: '"Inter", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', category: 'Sans-serif' },
  { label: 'Roboto', value: 'Roboto, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', category: 'Sans-serif' },
  { label: 'Open Sans', value: '"Open Sans", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', category: 'Sans-serif' },
  { label: 'Poppins', value: '"Poppins", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', category: 'Sans-serif' },
  { label: 'Lato', value: '"Lato", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', category: 'Sans-serif' },
  { label: 'Work Sans', value: '"Work Sans", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', category: 'Sans-serif' },
  { label: 'DM Sans', value: '"DM Sans", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', category: 'Sans-serif' },
  { label: 'Noto Sans', value: '"Noto Sans", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', category: 'Sans-serif' },
  { label: 'Figtree', value: '"Figtree", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', category: 'Sans-serif' },
  { label: 'Montserrat', value: '"Montserrat", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', category: 'Sans-serif' },
  { label: 'Nunito', value: '"Nunito", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', category: 'Sans-serif' },
  { label: 'Source Sans 3', value: '"Source Sans 3", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', category: 'Sans-serif' },
  { label: 'IBM Plex Sans', value: '"IBM Plex Sans", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', category: 'Sans-serif' },
  { label: 'Outfit', value: '"Outfit", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', category: 'Sans-serif' },
  { label: 'Space Grotesk', value: '"Space Grotesk", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', category: 'Sans-serif' },
  { label: 'Arial', value: 'Arial, Helvetica, sans-serif', category: 'Sans-serif' },
  { label: 'Helvetica Neue', value: '"Helvetica Neue", Arial, sans-serif', category: 'Sans-serif' },
  { label: 'Arial Black', value: '"Arial Black", "Arial Bold", Gadget, sans-serif', category: 'Sans-serif' },
  { label: 'Verdana', value: 'Verdana, Geneva, Tahoma, sans-serif', category: 'Sans-serif' },
  { label: 'Tahoma', value: 'Tahoma, Verdana, Segoe, sans-serif', category: 'Sans-serif' },
  { label: 'Trebuchet MS', value: '"Trebuchet MS", "Lucida Grande", "Lucida Sans Unicode", "Lucida Sans", Arial, sans-serif', category: 'Sans-serif' },
  { label: 'Gill Sans', value: '"Gill Sans", "Gill Sans MT", Calibri, "Trebuchet MS", sans-serif', category: 'Sans-serif' },
  { label: 'Segoe UI', value: '"Segoe UI", Tahoma, Geneva, Verdana, sans-serif', category: 'Sans-serif' },
  { label: 'Calibri', value: 'Calibri, "Gill Sans", "Trebuchet MS", sans-serif', category: 'Sans-serif' },
  // ── Serif ──
  { label: 'Times New Roman', value: '"Times New Roman", Times, serif', category: 'Serif' },
  { label: 'Georgia', value: 'Georgia, "Times New Roman", Times, serif', category: 'Serif' },
  { label: 'Garamond', value: 'Garamond, "Times New Roman", Times, serif', category: 'Serif' },
  { label: 'Palatino', value: '"Palatino Linotype", "Book Antiqua", Palatino, serif', category: 'Serif' },
  { label: 'Baskerville', value: 'Baskerville, "Times New Roman", Times, serif', category: 'Serif' },
  { label: 'Merriweather', value: '"Merriweather", Georgia, "Times New Roman", Times, serif', category: 'Serif' },
  { label: 'Playfair Display', value: '"Playfair Display", "Times New Roman", Times, serif', category: 'Serif' },
  { label: 'Source Serif 4', value: '"Source Serif 4", Georgia, "Times New Roman", Times, serif', category: 'Serif' },
  { label: 'Lora', value: '"Lora", Georgia, "Times New Roman", Times, serif', category: 'Serif' },
  { label: 'PT Serif', value: '"PT Serif", Georgia, "Times New Roman", Times, serif', category: 'Serif' },
  // ── Monospace ──
  { label: 'JetBrains Mono', value: '"JetBrains Mono", Menlo, Monaco, Consolas, "Liberation Mono", monospace', category: 'Monospace' },
  { label: 'Fira Code', value: '"Fira Code", Menlo, Monaco, Consolas, "Liberation Mono", monospace', category: 'Monospace' },
  { label: 'Source Code Pro', value: '"Source Code Pro", Menlo, Monaco, Consolas, "Liberation Mono", monospace', category: 'Monospace' },
  { label: 'Consolas', value: 'Consolas, "Liberation Mono", "Courier New", monospace', category: 'Monospace' },
  { label: 'Menlo', value: 'Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace', category: 'Monospace' },
  { label: 'Courier New', value: '"Courier New", Courier, monospace', category: 'Monospace' },
  { label: 'Lucida Console', value: '"Lucida Console", "Lucida Sans Typewriter", "Courier New", monospace', category: 'Monospace' },
  // ── Script / Decorative ──
  { label: 'Brush Script MT', value: '"Brush Script MT", cursive', category: 'Script' },
  { label: 'Bradley Hand', value: '"Bradley Hand", "Bradley Hand ITC", cursive', category: 'Script' },
  { label: 'Lucida Handwriting', value: '"Lucida Handwriting", "Lucida Calligraphy", cursive', category: 'Script' },
  { label: 'Pacifico', value: '"Pacifico", "Brush Script MT", cursive', category: 'Script' },
  { label: 'Dancing Script', value: '"Dancing Script", "Brush Script MT", cursive', category: 'Script' },
];

const THEME_MOODS = [
  { label: 'Midnight', description: 'moody nocturnal atmosphere' },
  { label: 'Editorial', description: 'clean editorial polish' },
  { label: 'Brutalist', description: 'assertive brutalist structure' },
  { label: 'Cinematic', description: 'dramatic cinematic contrast' },
  { label: 'Playful', description: 'playful energetic personality' },
  { label: 'Luxury', description: 'premium refined character' },
  { label: 'Organic', description: 'organic tactile warmth' },
  { label: 'Futurist', description: 'futuristic product aesthetic' },
  { label: 'Coastal', description: 'fresh coastal calm' },
  { label: 'Arcade', description: 'high-energy arcade glow' },
];

const THEME_COLOR_DIRECTIONS = [
  'deep charcoal with electric cyan and ember orange',
  'ivory and graphite with muted olive and brass',
  'obsidian with ultraviolet and acid lime',
  'sandstone with terracotta and faded teal',
  'porcelain white with cobalt and signal red',
  'ink blue with rose gold and soft plum',
  'forest green with cream and clay accents',
  'asphalt gray with neon pink and laser blue',
  'midnight navy with silver and glacier aqua',
  'espresso brown with butter cream and copper',
];

const THEME_TYPE_SYSTEMS = [
  'sharp grotesk headings with restrained body copy',
  'elegant serif headlines with understated sans text',
  'geometric sans typography with dense hierarchy',
  'wide display headings with compact utility text',
  'monospaced accents with polished humanist sans body',
];

const THEME_SURFACES = [
  'layered surfaces, visible card contrast, and clean borders',
  'soft elevated panels, inset controls, and restrained glass',
  'matte surfaces, bold dividers, and strong depth cues',
  'satin panels, subtle gradients, and high-clarity controls',
  'crisp sections, disciplined spacing, and tactile inputs',
];

const THEME_SHAPES = [
  'rounded-xl controls',
  'sharp square corners',
  'mixed radii with rounded cards and tighter inputs',
  'pill actions with structured panels',
  'confident medium radii and strong edge definition',
];

function buildThemeSuggestion(): ThemeSuggestion {
  const mood = THEME_MOODS[Math.floor(Math.random() * THEME_MOODS.length)];
  const colorDirection =
    THEME_COLOR_DIRECTIONS[Math.floor(Math.random() * THEME_COLOR_DIRECTIONS.length)];
  const typeSystem =
    THEME_TYPE_SYSTEMS[Math.floor(Math.random() * THEME_TYPE_SYSTEMS.length)];
  const surfaceSystem =
    THEME_SURFACES[Math.floor(Math.random() * THEME_SURFACES.length)];
  const shapeSystem = THEME_SHAPES[Math.floor(Math.random() * THEME_SHAPES.length)];
  const id = `${mood.label}-${colorDirection}-${typeSystem}-${surfaceSystem}-${shapeSystem}`
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-');

  return {
    id,
    label: `${mood.label} Mix`,
    prompt: `Create a distinctive UI theme with ${mood.description}, ${colorDirection}, ${typeSystem}, ${surfaceSystem}, and ${shapeSystem}. Generate both light and dark modes with shared brand DNA, high contrast, strong UI states, and no generic defaults. Include granular colors: primary, secondary, accent (with foreground pairs), background, foreground, surface, card, sidebar, popup, muted (with foregrounds), border, mutedBorder, input, ring, error/success/warning/info (with foregrounds), and 5 chart colors.`,
  };
}

function createDynamicSuggestions(count: number, exclude: ThemeSuggestion[] = []): ThemeSuggestion[] {
  const excludedIds = new Set(exclude.map((item) => item.id));
  const suggestions: ThemeSuggestion[] = [];

  while (suggestions.length < count) {
    const suggestion = buildThemeSuggestion();
    if (excludedIds.has(suggestion.id) || suggestions.some((item) => item.id === suggestion.id)) {
      continue;
    }
    suggestions.push(suggestion);
  }

  return suggestions;
}

function getThemeIcon(name?: string | null, description?: string | null): ThemeIconComponent {
  const text = `${name || ''} ${description || ''}`.toLowerCase();

  const iconMatchers: Array<[RegExp, ThemeIconComponent]> = [
    [/(midnight|night|nocturne|moon|lunar|eclipse|dark)/, Moon],
    [/(sun|solar|daybreak|dawn|light|bright)/, Sun],
    [/(ocean|sea|coast|wave|marine|aqua)/, Waves],
    [/(forest|organic|garden|nature|earth|moss|leaf|botanic)/, Trees],
    [/(ember|fire|flame|lava|inferno|burnt|volcanic)/, Flame],
    [/(luxury|gold|premium|opal|crystal|jewel|gem)/, Gem],
    [/(neon|electric|cyber|arcade|glow|futur|laser)/, Zap],
    [/(editorial|brutalist|metro|urban|architect|civic|concrete)/, Building2],
    [/(rose|romance|valentine|soft|dream|blush|love)/, Heart],
    [/(coffee|espresso|cafe|mocha|latte|roast)/, Coffee],
    [/(playful|game|arcade|pixel)/, Gamepad2],
    [/(shield|secure|security|ops|fortress)/, Shield],
  ];

  for (const [pattern, Icon] of iconMatchers) {
    if (pattern.test(text)) return Icon;
  }

  return Palette;
}

const TOKEN_TO_APP_VAR_MAP: Record<string, string[]> = {
  // Core
  primary: ['--primary'],
  primaryForeground: ['--primary-foreground'],
  secondary: ['--secondary'],
  secondaryForeground: ['--secondary-foreground'],
  accent: ['--accent'],
  accentForeground: ['--accent-foreground'],
  // Backgrounds & surfaces
  background: ['--background'],
  foreground: ['--foreground'],
  surface: ['--surface'],
  surfaceForeground: ['--surface-foreground'],
  card: ['--card'],
  cardForeground: ['--card-foreground'],
  sidebar: ['--sidebar'],
  sidebarForeground: ['--sidebar-foreground'],
  popup: ['--popup'],
  popupForeground: ['--popup-foreground'],
  muted: ['--muted'],
  mutedForeground: ['--muted-foreground'],
  // Text (legacy compat — only used if granular foreground tokens missing)
  text: ['--foreground', '--surface-foreground', '--card-foreground', '--sidebar-foreground', '--popup-foreground'],
  textMuted: ['--muted-foreground'],
  // Borders & inputs
  border: ['--border'],
  mutedBorder: ['--muted-border'],
  input: ['--input'],
  ring: ['--ring'],
  // Status
  error: ['--error'],
  errorForeground: ['--error-foreground'],
  success: ['--success'],
  successForeground: ['--success-foreground'],
  warning: ['--warning'],
  warningForeground: ['--warning-foreground'],
  info: ['--info'],
  infoForeground: ['--info-foreground'],
  // Charts
  chart1: ['--chart-color-1'],
  chart2: ['--chart-color-2'],
  chart3: ['--chart-color-3'],
  chart4: ['--chart-color-4'],
  chart5: ['--chart-color-5'],
};

const APP_TO_UNTITLED_UI_VAR_MAP: Record<string, string[]> = {
  '--background': ['--color-bg-primary', '--color-bg-primary_alt'],
  '--foreground': ['--color-fg-primary', '--color-text-primary'],
  '--surface': ['--color-bg-secondary', '--color-bg-secondary_subtle'],
  '--card': ['--color-bg-tertiary'],
  '--sidebar': ['--color-bg-secondary_alt'],
  '--popup': ['--color-bg-primary_hover', '--color-bg-overlay'],
  '--border': ['--color-border-primary', '--color-border-secondary'],
  '--primary': ['--color-bg-brand-solid'],
  '--muted': ['--color-bg-quaternary'],
  '--muted-foreground': ['--color-fg-quaternary', '--color-text-tertiary'],
  '--error': ['--color-error-600', '--color-error-500'],
  '--warning': ['--color-warning-600', '--color-warning-500'],
  '--success': ['--color-success-600', '--color-success-500'],
  '--info': ['--color-blue-600', '--color-blue-500'],
};

const APP_TO_AI_COLLAB_VAR_MAP: Record<string, string[]> = {
  '--background': ['--ai-collab-bg'],
  '--surface': ['--ai-collab-surface', '--ai-collab-ai-bg'],
  '--card': ['--ai-collab-surface-elevated'],
  '--border': ['--ai-collab-border', '--ai-collab-ai-border'],
  '--foreground': [
    '--ai-collab-text-primary',
    '--ai-collab-text-secondary',
    '--ai-collab-owner-text',
  ],
  '--muted-foreground': ['--ai-collab-text-muted'],
  '--primary': [
    '--ai-collab-accent',
    '--ai-collab-accent-hover',
    '--ai-collab-owner-bg',
    '--ai-collab-speaking',
    '--ai-collab-activity',
  ],
  '--warning': ['--ai-collab-typing'],
};

// Helper to emit CSS vars for a single token set (used by themeTokensToCss)
const emitTokenVars = (tokens: ThemeTokens, indent: string = '  '): string[] => {
  const lines: string[] = [];

  if (tokens.colors) {
    Object.entries(tokens.colors).forEach(([key, value]) => {
      const cssKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
      lines.push(`${indent}--theme-${cssKey}: ${value};`);

      const appVars = TOKEN_TO_APP_VAR_MAP[key];
      if (appVars) {
        appVars.forEach((varName) => {
          lines.push(`${indent}${varName}: ${value};`);

          const semanticVars = APP_TO_UNTITLED_UI_VAR_MAP[varName];
          if (semanticVars) {
            semanticVars.forEach((semanticVar) => {
              lines.push(`${indent}${semanticVar}: ${value};`);
            });
          }

          const aiCollabVars = APP_TO_AI_COLLAB_VAR_MAP[varName];
          if (aiCollabVars) {
            aiCollabVars.forEach((aiCollabVar) => {
              lines.push(`${indent}${aiCollabVar}: ${value};`);
            });
          }
        });
      }
    });
  }

  if (tokens.typography) {
    lines.push(`${indent}--theme-font-family: ${tokens.typography.fontFamily};`);
    lines.push(`${indent}--theme-heading-font-family: ${tokens.typography.headingFontFamily};`);
    lines.push(`${indent}--theme-base-font-size: ${tokens.typography.baseFontSize};`);
    lines.push(`${indent}--theme-line-height: ${tokens.typography.lineHeight};`);
    lines.push(`${indent}--theme-heading-line-height: ${tokens.typography.headingLineHeight};`);
    lines.push(`${indent}--font-family-base: ${tokens.typography.fontFamily};`);
    lines.push(`${indent}--font-family-heading: ${tokens.typography.headingFontFamily};`);
    if (tokens.typography.fontWeight) {
      lines.push(`${indent}--theme-font-weight: ${tokens.typography.fontWeight};`);
      lines.push(`${indent}--font-weight-base: ${tokens.typography.fontWeight};`);
    }
    if (tokens.typography.headingFontWeight) {
      lines.push(`${indent}--theme-heading-font-weight: ${tokens.typography.headingFontWeight};`);
      lines.push(`${indent}--font-weight-heading: ${tokens.typography.headingFontWeight};`);
    }
    if (tokens.typography.fontStyle) {
      lines.push(`${indent}--theme-font-style: ${tokens.typography.fontStyle};`);
      lines.push(`${indent}--font-style-base: ${tokens.typography.fontStyle};`);
    }
  }

  if (tokens.spacing) {
    Object.entries(tokens.spacing).forEach(([key, value]) => {
      lines.push(`${indent}--theme-spacing-${key}: ${value};`);
    });
  }

  if (tokens.borderRadius) {
    Object.entries(tokens.borderRadius).forEach(([key, value]) => {
      lines.push(`${indent}--theme-radius-${key}: ${value};`);
    });
    if (tokens.borderRadius.sm) lines.push(`${indent}--radius-sm: ${tokens.borderRadius.sm};`);
    if (tokens.borderRadius.md) lines.push(`${indent}--radius: ${tokens.borderRadius.md};`);
    if (tokens.borderRadius.lg) lines.push(`${indent}--radius-lg: ${tokens.borderRadius.lg};`);
    if (tokens.borderRadius.full) lines.push(`${indent}--radius-full: ${tokens.borderRadius.full};`);
  }

  if (tokens.shadows) {
    Object.entries(tokens.shadows).forEach(([key, value]) => {
      lines.push(`${indent}--theme-shadow-${key}: ${value};`);
    });
    if (tokens.shadows.sm) lines.push(`${indent}--shadow: ${tokens.shadows.sm};`);
    if (tokens.shadows.md) lines.push(`${indent}--shadow-md: ${tokens.shadows.md};`);
    if (tokens.shadows.lg) lines.push(`${indent}--shadow-lg: ${tokens.shadows.lg};`);
  }

  return lines;
};

// Helper to convert theme tokens to CSS variables (supports dual light+dark)
const themeTokensToCss = (tokens: ThemeTokens, darkTokens?: ThemeTokens): string => {
  const lines: string[] = [];

  // Light mode (default :root)
  lines.push(':root {');
  lines.push(...emitTokenVars(tokens));
  lines.push('}');

  // Dark mode variant
  if (darkTokens) {
    lines.push('');
    lines.push(':root.dark, :root[data-theme="dark"], body.dark, body[data-theme="dark"] {');
    lines.push(...emitTokenVars(darkTokens));
    lines.push('}');
    lines.push('');
    lines.push('@media (prefers-color-scheme: dark) {');
    lines.push('  :root:not(.light):not([data-theme="light"]) {');
    lines.push(...emitTokenVars(darkTokens, '    '));
    lines.push('  }');
    lines.push('}');
  }

  lines.push('');
  lines.push('/* Apply theme colors to common elements */');
  lines.push('body {');
  lines.push('  font-family: var(--theme-font-family);');
  lines.push('  font-size: var(--theme-base-font-size);');
  lines.push('  line-height: var(--theme-line-height);');
  lines.push('  background-color: var(--theme-background);');
  lines.push('  color: var(--theme-text);');
  lines.push('}');
  lines.push('');
  lines.push('h1, h2, h3, h4, h5, h6 {');
  lines.push('  font-family: var(--theme-heading-font-family);');
  lines.push('  line-height: var(--theme-heading-line-height);');
  lines.push('}');

  return lines.join('\n');
};

export const ThemesPanel: React.FC<ThemesPanelProps> = ({ sessionId, isOwner = false, hideProjectScope = false }) => {
  // When used from account settings, sessionId is "__global__" which isn't a real session
  const isGlobal = sessionId === '__global__';
  const effectiveSessionId = isGlobal ? undefined : sessionId;

  const {
    data: themesData,
    isLoading: themesLoading,
    isError: themesError,
    error: themesErrorMessage,
    refetch: refetchThemes,
  } = useThemes(null);
  const {
    data: imagesData,
    isLoading: imagesLoading,
    refetch: refetchImages,
  } = useGeneratedImages(effectiveSessionId ?? sessionId);

  const { data: filesData } = useSessionFiles(effectiveSessionId ?? sessionId);
  const createFileMutation = useCreateFile();
  const updateFileMutation = useUpdateFile();

  const createThemeMutation = useCreateTheme();
  const updateThemeMutation = useUpdateTheme();
  const deleteThemeMutation = useDeleteTheme();
  const {
    applySiteTheme,
    clearSiteTheme,
    isApplying: isSiteThemeApplying,
    currentTheme: activeSiteTheme,
    enabled: siteThemeEnabled,
    toggleEnabled: toggleSiteTheme,
  } = useSiteTheme();
  const generateThemeMutation = useGenerateTheme();
  const generateImageMutation = useGenerateImage();

  const [activeTab, setActiveTab] = useState<TabType>('themes');
  const [generatePrompt, setGeneratePrompt] = useState('');
  const [imagePrompt, setImagePrompt] = useState('');
  const [generatedTheme, setGeneratedTheme] = useState<GeneratedTheme | null>(null);
  const [previewMode, setPreviewMode] = useState<'light' | 'dark'>('light');
  const [generatedThemeImage, setGeneratedThemeImage] = useState<string | null>(null);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [themeName, setThemeName] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<ThemeOutput | null>(null);
  const [autoGenerateImage, setAutoGenerateImage] = useState(false);
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);
  const [isApplyingTheme, setIsApplyingTheme] = useState(false);
  const [appliedProjectThemeId, setAppliedProjectThemeId] = useState<string | null>(() => {
    try {
      return localStorage.getItem(`tasking-session-theme:${sessionId}`) || null;
    } catch {
      return null;
    }
  });
  const [appliedProjectFile, setAppliedProjectFile] = useState<string | null>(() => {
    try {
      return localStorage.getItem(`tasking-session-theme-file:${sessionId}`) || null;
    } catch {
      return null;
    }
  });
  const [savingImageId, setSavingImageId] = useState<string | null>(null);
  const [imageProvider, setImageProvider] = useState<'modelslab' | 'flux' | 'gemini'>('modelslab');
  const [appliedSiteThemeId, setAppliedSiteThemeId] = useState<string | null>(null);
  const [visibleSuggestions, setVisibleSuggestions] = useState<ThemeSuggestion[]>(() =>
    createDynamicSuggestions(6),
  );
  const [isDiceRolling, setIsDiceRolling] = useState(false);
  const [previousSiteThemeId, setPreviousSiteThemeId] = useState<string | null>(null);

  // ── Standalone typography overrides (persisted in localStorage) ──
  const TYPO_KEY = 'tasking-user-typography';
  const loadTypo = (): { fontFamily: string; fontSize: string; fontStyle: string } => {
    try {
      const raw = localStorage.getItem(TYPO_KEY);
      if (raw) return JSON.parse(raw);
    } catch {}
    return { fontFamily: '', fontSize: '', fontStyle: '' };
  };
  const [typoFamily, setTypoFamily] = useState(() => loadTypo().fontFamily);
  const [typoSize, setTypoSize] = useState(() => loadTypo().fontSize);
  const [typoStyle, setTypoStyle] = useState(() => loadTypo().fontStyle);

  const applyTypoOverrides = useCallback((family: string, size: string, style: string) => {
    const setVar = (v: string, val: string) => {
      document.documentElement.style.setProperty(v, val);
      document.body.style.setProperty(v, val);
    };
    const removeVar = (v: string) => {
      document.documentElement.style.removeProperty(v);
      document.body.style.removeProperty(v);
    };
    if (family) { setVar('--font-family-base', family); setVar('--font-family-heading', family); }
    else { removeVar('--font-family-base'); removeVar('--font-family-heading'); }
    if (size) { setVar('--theme-base-font-size', size); document.documentElement.style.fontSize = size; }
    else { removeVar('--theme-base-font-size'); document.documentElement.style.fontSize = ''; }
    if (style) { setVar('--font-style-base', style); }
    else { removeVar('--font-style-base'); }
    try {
      if (family || size || style) {
        localStorage.setItem(TYPO_KEY, JSON.stringify({ fontFamily: family, fontSize: size, fontStyle: style }));
      } else {
        localStorage.removeItem(TYPO_KEY);
      }
    } catch {}
  }, []);

  // Apply on mount + load saved font
  useEffect(() => {
    if (typoFamily) {
      const font = FONT_OPTIONS.find((f) => f.value === typoFamily);
      if (font) loadGoogleFont(font.label);
    }
    applyTypoOverrides(typoFamily, typoSize, typoStyle);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist project theme assignment to localStorage
  useEffect(() => {
    try {
      if (appliedProjectThemeId) {
        localStorage.setItem(`tasking-session-theme:${sessionId}`, appliedProjectThemeId);
      } else {
        localStorage.removeItem(`tasking-session-theme:${sessionId}`);
      }
      if (appliedProjectFile) {
        localStorage.setItem(`tasking-session-theme-file:${sessionId}`, appliedProjectFile);
      } else {
        localStorage.removeItem(`tasking-session-theme-file:${sessionId}`);
      }
    } catch {}
  }, [sessionId, appliedProjectThemeId, appliedProjectFile]);

  const handleDiceRoll = useCallback(async () => {
    setIsDiceRolling(true);

    try {
      const [randomSuggestion] = createDynamicSuggestions(1, visibleSuggestions);

      // Generate a real AI theme
      const result = await generateThemeMutation.mutateAsync({
        sessionId: effectiveSessionId,
        prompt: randomSuggestion.prompt,
      });

      // Set the generated theme so user can review/save it
      setGeneratedTheme(result.theme);
      setThemeName(result.theme.name);
      setGeneratedThemeImage(null);
      setGeneratePrompt(randomSuggestion.prompt);
      // Auto-open save dialog
      setShowSaveDialog(true);

      toast.success(`Generated theme: ${result.theme.name}`, {
        description: 'Save or apply it now.',
      });
      requestAnimationFrame(() => {
        const previewEl = document.querySelector('[data-theme-preview]');
        previewEl?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      });

      // Shuffle the visible suggestions for next time
      setVisibleSuggestions(createDynamicSuggestions(6, [randomSuggestion]));
    } catch (err) {
      toast.error('Failed to generate theme. Try again.');
      console.error('Dice roll theme generation error:', err);
    } finally {
      setIsDiceRolling(false);
    }
  }, [visibleSuggestions, sessionId, generateThemeMutation]);

  const themes = themesData?.themes || [];
  const images = imagesData?.images || [];

  const handleGenerateTheme = async () => {
    if (!generatePrompt.trim()) {
      toast.error('Please enter a description for your theme');
      return;
    }

    try {
      // Generate theme tokens first
      const result = await generateThemeMutation.mutateAsync({
        sessionId: effectiveSessionId,
        prompt: generatePrompt.trim(),
      });
      setGeneratedTheme(result.theme);
      setThemeName(result.theme.name);
      setGeneratedThemeImage(null);
      // Auto-open save dialog so user can save/apply immediately
      setShowSaveDialog(true);
      toast.success(`Theme "${result.theme.name}" generated! Save or apply it now.`);
      // Auto-scroll to the preview section
      requestAnimationFrame(() => {
        const previewEl = document.querySelector('[data-theme-preview]');
        previewEl?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      });

      // Auto-generate image if enabled and we have an imagePrompt
      if (autoGenerateImage && result.theme.imagePrompt) {
        setIsGeneratingImage(true);
        try {
          const imageResult = await generateImageMutation.mutateAsync({
            sessionId: effectiveSessionId ?? sessionId,
            prompt: result.theme.imagePrompt,
            width: 1024,
            height: 1024,
            provider: imageProvider,
            saveToSession: false,
            mode: 'generate',
            logoPosition: 'bottom-right',
            logoScale: 0.15,
          });
          setGeneratedThemeImage(imageResult.image.imageUrl);
          toast.success('Theme image generated!');
        } catch (imgErr) {
          console.error('Failed to generate theme image:', imgErr);
          toast.error(
            'Theme created, but image generation failed. You can generate images separately.',
          );
        } finally {
          setIsGeneratingImage(false);
        }
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to generate theme');
    }
  };

  const handleGenerateThemeImage = async () => {
    if (!generatedTheme?.imagePrompt) {
      toast.error('No image prompt available');
      return;
    }

    setIsGeneratingImage(true);
    try {
      const imageResult = await generateImageMutation.mutateAsync({
        sessionId: effectiveSessionId ?? sessionId,
        prompt: generatedTheme.imagePrompt,
        width: 1024,
        height: 1024,
        provider: imageProvider,
        saveToSession: false,
        mode: 'generate',
        logoPosition: 'bottom-right',
        logoScale: 0.15,
      });
      setGeneratedThemeImage(imageResult.image.imageUrl);
      toast.success('Theme image generated!');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to generate image');
    } finally {
      setIsGeneratingImage(false);
    }
  };

  // Apply theme CSS to project (saves to DB via session files API)
  const handleApplyThemeToProject = useCallback(
    async (theme: ThemeOutput, _themeImage?: string) => {
      setIsApplyingTheme(true);
      setAppliedProjectThemeId(null);
      setAppliedProjectFile(null);

      try {
        const tokens = JSON.parse(theme.tokens) as ThemeTokens;

        // Look for paired dark theme
        let darkTokens: ThemeTokens | undefined;
        const pairedId = (theme as any).pairedThemeId;
        if (pairedId) {
          const pairedTheme = themes.find((t) => t.id === pairedId);
          if (pairedTheme) {
            try {
              darkTokens = JSON.parse(pairedTheme.tokens) as ThemeTokens;
            } catch {}
          }
        }

        const cssContent = themeTokensToCss(tokens, darkTokens);

        const sessionFiles = filesData?.files || [];

        // Helper: find a session file by its full path
        const findFile = (fullPath: string) => {
          const parts = fullPath.split('/').filter(Boolean);
          const fileName = parts.pop() || '';
          const filePath = '/' + (parts.length > 0 ? parts.join('/') + '/' : '');
          return sessionFiles.find(
            (f) =>
              f.fileName === fileName &&
              (f.filePath === filePath ||
                f.filePath === filePath.replace(/\/$/, '') ||
                (!f.filePath && filePath === '/')),
          );
        };

        // Helper: write or update a session file
        const writeSessionFile = async (fullPath: string, content: string) => {
          const existing = findFile(fullPath);
          if (existing) {
            await updateFileMutation.mutateAsync({ id: existing.id, content });
          } else {
            const parts = fullPath.split('/').filter(Boolean);
            const fileName = parts.pop() || 'theme.css';
            const filePath = '/' + (parts.length > 0 ? parts.join('/') + '/' : '');
            await createFileMutation.mutateAsync({
              sessionId: effectiveSessionId ?? sessionId,
              fileName,
              filePath,
              content,
              language: 'css',
            });
          }
        };

        const possibleFiles = [
          '/src/theme.css',
          '/src/styles/theme.css',
          '/app/theme.css',
          '/styles/theme.css',
          '/theme.css',
        ];

        // Check for existing theme file or create in src/
        let targetFile = '/src/theme.css';
        for (const file of possibleFiles) {
          if (findFile(file)) {
            targetFile = file;
            break;
          }
        }

        // Write the theme CSS
        await writeSessionFile(targetFile, cssContent);
        setAppliedProjectThemeId(theme.id);
        setAppliedProjectFile(targetFile);

        // Check if we need to import it into index.css or main.css
        const indexFiles = [
          '/src/index.css',
          '/src/main.css',
          '/app/globals.css',
          '/styles/globals.css',
        ];
        for (const indexFile of indexFiles) {
          const existing = findFile(indexFile);
          if (existing) {
            const content = String(existing.content || '');
            const importStatement = `@import '${targetFile.replace('/src/', './')}';`;

            if (!content.includes('theme.css')) {
              // Add import at the top
              const updatedContent = `${importStatement}\n${content}`;
              await writeSessionFile(indexFile, updatedContent);
              toast.success(`Theme applied to ${targetFile} and imported in ${indexFile}`, {
                description: 'Use CSS variables like var(--theme-primary) in your project styles',
              });
              return;
            }
            break;
          }
        }

        toast.success(`Theme applied to ${targetFile}`, {
          description: 'Use CSS variables like var(--theme-primary) in your project styles',
        });
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to apply theme');
      } finally {
        setIsApplyingTheme(false);
      }
    },
    [filesData, sessionId, createFileMutation, updateFileMutation],
  );

  // Apply theme to the tasking.tech site UI (saves to DB + localStorage cache)
  const handleApplyThemeToSite = useCallback(
    async (theme: ThemeOutput) => {
      // Save previous theme for undo
      const prevId = appliedSiteThemeId || activeSiteTheme?.id || null;
      setPreviousSiteThemeId(prevId);
      setAppliedSiteThemeId(null);
      try {
        const tokens = JSON.parse(theme.tokens) as ThemeTokens;

        // Look for paired dark theme
        let pairedTokens: ThemeTokens | undefined;
        const pairedId = (theme as any).pairedThemeId;
        if (pairedId) {
          const pairedTheme = themes.find((t) => t.id === pairedId);
          if (pairedTheme) {
            try {
              pairedTokens = JSON.parse(pairedTheme.tokens) as ThemeTokens;
            } catch {}
          }
        }

        await applySiteTheme(theme.id, tokens, theme.name, pairedTokens);
        setAppliedSiteThemeId(theme.id);
        toast.success(`Site theme set to "${theme.name}"`, {
          description: 'Applies across all of your sessions. Saved to your account.',
        });
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to apply site theme');
      }
    },
    [applySiteTheme, themes, appliedSiteThemeId, activeSiteTheme],
  );

  const handleSaveTheme = async () => {
    if (!generatedTheme) return;

    try {
      await createThemeMutation.mutateAsync({
        name: themeName || generatedTheme.name,
        description: generatedTheme.description,
        tokens: generatedTheme.tokens,
        darkTokens: generatedTheme.darkTokens,
        imagePrompt: generatedTheme.imagePrompt,
        setActive: true,
        saveGlobal: true,
        isPublic: false,
      });
      toast.success('Theme saved and activated globally!');
      setShowSaveDialog(false);
      setGeneratedTheme(null);
      setGeneratePrompt('');
      setThemeName('');
      refetchThemes();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save theme');
    }
  };

  const handleActivateTheme = async (theme: ThemeOutput) => {
    try {
      await updateThemeMutation.mutateAsync({
        themeId: theme.id,
        setActive: true,
      });

      toast.success('Theme activated');
      refetchThemes();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to activate theme');
    }
  };

  const handleDeleteTheme = async () => {
    if (!deleteConfirm) return;

    try {
      await deleteThemeMutation.mutateAsync({ themeId: deleteConfirm.id });
      toast.success('Theme deleted');
      setDeleteConfirm(null);
      refetchThemes();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete theme');
    }
  };

  const handleGenerateImage = async () => {
    if (!imagePrompt.trim()) {
      toast.error('Please enter a prompt for your image');
      return;
    }

    try {
      const result = await generateImageMutation.mutateAsync({
        sessionId: effectiveSessionId ?? sessionId,
        prompt: imagePrompt.trim(),
        width: 1024,
        height: 1024,
        provider: imageProvider,
        saveToSession: true,
        mode: 'generate',
        logoPosition: 'bottom-right',
        logoScale: 0.15,
      });
      toast.success('Image generated!');
      setImagePrompt('');
      refetchImages();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to generate image');
    }
  };

  // Save generated image to project files
  const handleSaveImageToFiles = async (image: ImageOutput) => {
    setSavingImageId(image.id);
    try {
      toast.info('Image saved to theme database.');
    } catch (err) {
      console.error('Failed to save image to files:', err);
      toast.error('Failed to save image to files');
    } finally {
      setSavingImageId(null);
    }
  };

  const parseTokens = (tokensJson: string): ThemeTokens | null => {
    try {
      return JSON.parse(tokensJson) as ThemeTokens;
    } catch {
      return null;
    }
  };

  // Debounced color change for saved themes
  const colorChangeTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const genColorTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleSavedThemeColorChange = useCallback(
    (theme: ThemeOutput, colorKey: string, newValue: string) => {
      if (colorChangeTimerRef.current) clearTimeout(colorChangeTimerRef.current);
      colorChangeTimerRef.current = setTimeout(async () => {
        try {
          const currentTokens = JSON.parse(theme.tokens) as ThemeTokens;
          const updatedTokens = {
            ...currentTokens,
            colors: { ...currentTokens.colors, [colorKey]: newValue },
          };
          await updateThemeMutation.mutateAsync({
            themeId: theme.id,
            tokens: updatedTokens,
          });
          // Delay refetch so color picker isn't unmounted mid-interaction
          setTimeout(() => refetchThemes(), 1000);
        } catch (err) {
          toast.error('Failed to update color');
        }
      }, 800);
    },
    [updateThemeMutation, refetchThemes],
  );

  const typoChangeTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleSavedThemeTypographyChange = useCallback(
    (theme: ThemeOutput, field: string, newValue: string) => {
      if (typoChangeTimerRef.current) clearTimeout(typoChangeTimerRef.current);
      typoChangeTimerRef.current = setTimeout(async () => {
        try {
          const currentTokens = JSON.parse(theme.tokens) as ThemeTokens;
          const updatedTokens = {
            ...currentTokens,
            typography: { ...currentTokens.typography, [field]: newValue },
          };
          await updateThemeMutation.mutateAsync({
            themeId: theme.id,
            tokens: updatedTokens,
          });
          setTimeout(() => refetchThemes(), 1000);
        } catch (err) {
          toast.error('Failed to update typography');
        }
      }, 800);
    },
    [updateThemeMutation, refetchThemes],
  );

  const renderColorPreview = (
    tokens: ThemeTokens | null,
    label?: string,
    editableTheme?: ThemeOutput,
  ) => {
    if (!tokens?.colors) return null;
    // Show all colors that have values, sorted by category relevance
    const previewKeys = Object.keys(tokens.colors).filter((k) => (tokens.colors as any)[k]);
    return (
      <div className={styles.colorPreviewRow}>
        {label && (
          <span className={styles.colorPreviewLabel}>
            {label === 'Light' ? <Sun size={12} /> : label === 'Dark' ? <Moon size={12} /> : null}
            {label}
          </span>
        )}
        <div className={styles.colorPreview}>
          {previewKeys.map((key) => {
            const color = (tokens.colors as any)[key];
            const meta = COLOR_META[key];
            return color ? (
              <Tooltip key={key}>
                <TooltipTrigger asChild>
                  {editableTheme ? (
                    <div className={styles.colorPickerSwatch} style={{ backgroundColor: color }}>
                      <input
                        type="color"
                        className={styles.colorPickerInput}
                        defaultValue={color}
                        onInput={(e) => {
                          const swatch = (e.target as HTMLElement).parentElement;
                          if (swatch) swatch.style.backgroundColor = (e.target as HTMLInputElement).value;
                        }}
                        onChange={(e) =>
                          handleSavedThemeColorChange(editableTheme, key, e.target.value)
                        }
                      />
                    </div>
                  ) : (
                    <div className={styles.colorSwatch} style={{ backgroundColor: color }} />
                  )}
                </TooltipTrigger>
                <TooltipContent>
                  <strong>{meta?.label || key}</strong>: {color}
                  {meta?.desc ? <><br />{meta.desc}</> : ''}
                  {editableTheme ? <><br /><em>Click to edit</em></> : ''}
                </TooltipContent>
              </Tooltip>
            ) : null;
          })}
        </div>
      </div>
    );
  };

  const renderGeneratedThemePreview = () => {
    if (!generatedTheme) return null;
    const normalizedGeneratedThemeImage = normalizeImageDataUrl(generatedThemeImage);
    const GeneratedThemeIcon = getThemeIcon(generatedTheme.name, generatedTheme.description);

    return (
      <div className={styles.previewSection} data-theme-preview>
        <div className={styles.previewHeader}>
          <span>Generated Theme Preview</span>
          <Button size="sm" onClick={() => setShowSaveDialog(true)}>
            <Plus size={14} />
            Save Theme
          </Button>
        </div>
        <div className={styles.previewContent}>
          <h4 className={styles.previewName}>
            <GeneratedThemeIcon size={16} style={{ marginRight: 6, verticalAlign: 'text-bottom' }} />
            {generatedTheme.name}
          </h4>
          <p className={styles.previewDescription}>{generatedTheme.description}</p>

          {/* Generated Image Preview */}
          {(normalizedGeneratedThemeImage || isGeneratingImage) && (
            <div className={styles.tokenSection}>
              <div className={styles.tokenLabel}>
                <ImagePlus size={12} style={{ marginRight: 4 }} />
                Generated Theme Image
              </div>
              {isGeneratingImage ? (
                <div className={styles.imageGenerating}>
                  <Loader2 size={24} className={styles.spinnerIcon} />
                  <span>Generating matching image...</span>
                </div>
              ) : normalizedGeneratedThemeImage ? (
                <img
                  src={normalizedGeneratedThemeImage}
                  alt="Generated theme visual"
                  className={styles.generatedThemeImage}
                />
              ) : null}
            </div>
          )}

          {/* Generate Image Button if no image yet */}
          {!normalizedGeneratedThemeImage && !isGeneratingImage && generatedTheme.imagePrompt && (
            <div className={styles.tokenSection}>
              <Button
                variant="outline"
                size="sm"
                onClick={handleGenerateThemeImage}
                disabled={isGeneratingImage}
              >
                <Wand2 size={14} />
                Generate Theme Image
              </Button>
              <p className={styles.hint}>Uses AI to create a matching visual for your theme</p>
            </div>
          )}

          <div className={styles.tokenSection}>
            <div className={styles.tokenLabel}>
              Colors
              {generatedTheme.darkTokens && (
                <div className={styles.modeToggle}>
                  <button
                    type="button"
                    className={`${styles.modeToggleBtn} ${previewMode === 'light' ? styles.modeToggleActive : ''}`}
                    onClick={() => setPreviewMode('light')}
                    title="Light mode"
                  >
                    <Sun size={13} /> Light
                  </button>
                  <button
                    type="button"
                    className={`${styles.modeToggleBtn} ${previewMode === 'dark' ? styles.modeToggleActive : ''}`}
                    onClick={() => setPreviewMode('dark')}
                    title="Dark mode"
                  >
                    <Moon size={13} /> Dark
                  </button>
                </div>
              )}
            </div>
            {(() => {
              const colors = previewMode === 'dark' && generatedTheme.darkTokens
                ? generatedTheme.darkTokens.colors
                : generatedTheme.tokens.colors;
              const entries = Object.entries(colors).filter(([, v]) => v);
              // Group by category
              const grouped: Record<string, [string, string][]> = {};
              for (const [name, value] of entries) {
                const meta = COLOR_META[name];
                const cat = meta?.cat || 'Other';
                if (!grouped[cat]) grouped[cat] = [];
                grouped[cat].push([name, value as string]);
              }
              return COLOR_CATEGORIES.filter((c) => grouped[c]?.length).map((cat) => (
                <div key={cat} className={styles.colorCategory}>
                  <span className={styles.colorCategoryLabel}>{cat}</span>
                  <div className={styles.colorGrid}>
                    {grouped[cat].map(([name, value]) => {
                      const meta = COLOR_META[name];
                      return (
                        <Tooltip key={name}>
                          <TooltipTrigger asChild>
                            <div className={styles.colorItem}>
                              <div
                                className={styles.colorPickerSwatch}
                                style={{ backgroundColor: value }}
                              >
                                <input
                                  type="color"
                                  className={styles.colorPickerInput}
                                  defaultValue={value}
                                  onInput={(e) => {
                                    const swatch = (e.target as HTMLElement).parentElement;
                                    if (swatch) swatch.style.backgroundColor = (e.target as HTMLInputElement).value;
                                  }}
                                  onChange={(e) => {
                                    const newVal = e.target.value;
                                    if (genColorTimerRef.current) clearTimeout(genColorTimerRef.current);
                                    genColorTimerRef.current = setTimeout(() => {
                                      setGeneratedTheme((prev) => {
                                        if (!prev) return prev;
                                        if (previewMode === 'dark' && prev.darkTokens) {
                                          return {
                                            ...prev,
                                            darkTokens: {
                                              ...prev.darkTokens,
                                              colors: { ...prev.darkTokens.colors, [name]: newVal },
                                            },
                                          };
                                        }
                                        return {
                                          ...prev,
                                          tokens: {
                                            ...prev.tokens,
                                            colors: { ...prev.tokens.colors, [name]: newVal },
                                          },
                                        };
                                      });
                                    }, 100);
                                  }}
                                />
                              </div>
                              <span className={styles.colorName}>{meta?.label || name}</span>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>{meta?.desc || name}</TooltipContent>
                        </Tooltip>
                      );
                    })}
                  </div>
                </div>
              ));
            })()}
          </div>

          <div className={styles.tokenSection}>
            <div className={styles.tokenLabel}>Typography</div>
            <div className={styles.typographyControls}>
              <div className={styles.fontField}>
                <label className={styles.fontLabel}>Body Font</label>
                <select
                  className={styles.fontSelect}
                  value={generatedTheme.tokens.typography.fontFamily}
                  onChange={(e) => {
                    const val = e.target.value;
                    const font = FONT_OPTIONS.find((f) => f.value === val);
                    if (font) loadGoogleFont(font.label);
                    setGeneratedTheme((prev) => prev ? {
                      ...prev,
                      tokens: { ...prev.tokens, typography: { ...prev.tokens.typography, fontFamily: val } },
                      ...(prev.darkTokens ? { darkTokens: { ...prev.darkTokens, typography: { ...prev.darkTokens.typography, fontFamily: val } } } : {}),
                    } : prev);
                  }}
                  style={{ fontFamily: generatedTheme.tokens.typography.fontFamily }}
                >
                  {(['Sans-serif', 'Serif', 'Monospace', 'Script'] as const).map((cat) => {
                    const fonts = FONT_OPTIONS.filter((f) => f.category === cat);
                    if (fonts.length === 0) return null;
                    return (
                      <optgroup key={cat} label={cat}>
                        {fonts.map((f) => (
                          <option key={f.value} value={f.value} style={{ fontFamily: f.value }}>{f.label}</option>
                        ))}
                      </optgroup>
                    );
                  })}
                </select>
                <span className={styles.fontPreview} style={{ fontFamily: generatedTheme.tokens.typography.fontFamily, fontWeight: Number(generatedTheme.tokens.typography.fontWeight || '400'), fontStyle: (generatedTheme.tokens.typography.fontStyle || 'normal') as any }}>
                  The quick brown fox jumps over the lazy dog
                </span>
              </div>
              <div className={styles.fontField}>
                <label className={styles.fontLabel}>Heading Font</label>
                <select
                  className={styles.fontSelect}
                  value={generatedTheme.tokens.typography.headingFontFamily}
                  onChange={(e) => {
                    const val = e.target.value;
                    const font = FONT_OPTIONS.find((f) => f.value === val);
                    if (font) loadGoogleFont(font.label);
                    setGeneratedTheme((prev) => prev ? {
                      ...prev,
                      tokens: { ...prev.tokens, typography: { ...prev.tokens.typography, headingFontFamily: val } },
                      ...(prev.darkTokens ? { darkTokens: { ...prev.darkTokens, typography: { ...prev.darkTokens.typography, headingFontFamily: val } } } : {}),
                    } : prev);
                  }}
                  style={{ fontFamily: generatedTheme.tokens.typography.headingFontFamily }}
                >
                  {(['Sans-serif', 'Serif', 'Monospace', 'Script'] as const).map((cat) => {
                    const fonts = FONT_OPTIONS.filter((f) => f.category === cat);
                    if (fonts.length === 0) return null;
                    return (
                      <optgroup key={cat} label={cat}>
                        {fonts.map((f) => (
                          <option key={f.value} value={f.value} style={{ fontFamily: f.value }}>{f.label}</option>
                        ))}
                      </optgroup>
                    );
                  })}
                </select>
                <span className={styles.fontPreview} style={{ fontFamily: generatedTheme.tokens.typography.headingFontFamily, fontWeight: Number(generatedTheme.tokens.typography.headingFontWeight || '600'), fontSize: '1rem' }}>
                  Heading Preview Text
                </span>
              </div>
              <div className={styles.fontField}>
                <label className={styles.fontLabel}>Base Size</label>
                <select
                  className={styles.fontSelect}
                  value={generatedTheme.tokens.typography.baseFontSize}
                  onChange={(e) => {
                    const val = e.target.value;
                    setGeneratedTheme((prev) => prev ? {
                      ...prev,
                      tokens: { ...prev.tokens, typography: { ...prev.tokens.typography, baseFontSize: val } },
                      ...(prev.darkTokens ? { darkTokens: { ...prev.darkTokens, typography: { ...prev.darkTokens.typography, baseFontSize: val } } } : {}),
                    } : prev);
                  }}
                >
                  {['12px', '13px', '14px', '15px', '16px', '18px'].map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              <div className={styles.fontField}>
                <label className={styles.fontLabel}>Body Font Weight</label>
                <select
                  className={styles.fontSelect}
                  value={generatedTheme.tokens.typography.fontWeight || '400'}
                  onChange={(e) => {
                    const val = e.target.value;
                    setGeneratedTheme((prev) => prev ? {
                      ...prev,
                      tokens: { ...prev.tokens, typography: { ...prev.tokens.typography, fontWeight: val } },
                      ...(prev.darkTokens ? { darkTokens: { ...prev.darkTokens, typography: { ...prev.darkTokens.typography, fontWeight: val } } } : {}),
                    } : prev);
                  }}
                >
                  {[
                    { value: '300', label: 'Light' },
                    { value: '400', label: 'Regular' },
                    { value: '500', label: 'Medium' },
                    { value: '600', label: 'Semibold' },
                    { value: '700', label: 'Bold' },
                  ].map((w) => (
                    <option key={w.value} value={w.value}>{w.label} ({w.value})</option>
                  ))}
                </select>
                <span className={styles.fontPreview} style={{ fontFamily: generatedTheme.tokens.typography.fontFamily, fontWeight: Number(generatedTheme.tokens.typography.fontWeight || '400') }}>
                  Body weight preview
                </span>
              </div>
              <div className={styles.fontField}>
                <label className={styles.fontLabel}>Heading Font Weight</label>
                <select
                  className={styles.fontSelect}
                  value={generatedTheme.tokens.typography.headingFontWeight || '600'}
                  onChange={(e) => {
                    const val = e.target.value;
                    setGeneratedTheme((prev) => prev ? {
                      ...prev,
                      tokens: { ...prev.tokens, typography: { ...prev.tokens.typography, headingFontWeight: val } },
                      ...(prev.darkTokens ? { darkTokens: { ...prev.darkTokens, typography: { ...prev.darkTokens.typography, headingFontWeight: val } } } : {}),
                    } : prev);
                  }}
                >
                  {[
                    { value: '400', label: 'Regular' },
                    { value: '500', label: 'Medium' },
                    { value: '600', label: 'Semibold' },
                    { value: '700', label: 'Bold' },
                    { value: '800', label: 'Extra Bold' },
                    { value: '900', label: 'Black' },
                  ].map((w) => (
                    <option key={w.value} value={w.value}>{w.label} ({w.value})</option>
                  ))}
                </select>
                <span className={styles.fontPreview} style={{ fontFamily: generatedTheme.tokens.typography.headingFontFamily, fontWeight: Number(generatedTheme.tokens.typography.headingFontWeight || '600'), fontSize: '1rem' }}>
                  Heading weight preview
                </span>
              </div>
              <div className={styles.fontField}>
                <label className={styles.fontLabel}>Font Style</label>
                <select
                  className={styles.fontSelect}
                  value={generatedTheme.tokens.typography.fontStyle || 'normal'}
                  onChange={(e) => {
                    const val = e.target.value;
                    setGeneratedTheme((prev) => prev ? {
                      ...prev,
                      tokens: { ...prev.tokens, typography: { ...prev.tokens.typography, fontStyle: val } },
                      ...(prev.darkTokens ? { darkTokens: { ...prev.darkTokens, typography: { ...prev.darkTokens.typography, fontStyle: val } } } : {}),
                    } : prev);
                  }}
                >
                  {[
                    { value: 'normal', label: 'Normal' },
                    { value: 'italic', label: 'Italic' },
                  ].map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
                <span className={styles.fontPreview} style={{ fontFamily: generatedTheme.tokens.typography.fontFamily, fontStyle: generatedTheme.tokens.typography.fontStyle || 'normal' }}>
                  Style preview text
                </span>
              </div>
            </div>
          </div>

          {generatedTheme.imagePrompt && (
            <div className={styles.tokenSection}>
              <div className={styles.tokenLabel}>Image Prompt</div>
              <p className={styles.typographyPreview}>{generatedTheme.imagePrompt}</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (themesLoading && activeTab === 'themes') {
    return (
      <div className={styles.container}>
        <Skeleton style={{ width: '100%', height: '200px' }} />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.titleRow}>
          <h3 className={styles.title}>
            <Palette size={18} />
            Themes & Images
          </h3>
          <div className={styles.headerActions}>
            {activeSiteTheme && (
              <>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      className={styles.themeToggle}
                      onClick={() => toggleSiteTheme(!siteThemeEnabled)}
                      aria-label={siteThemeEnabled ? 'Disable site theme' : 'Enable site theme'}
                    >
                      <span
                        className={`${styles.themeToggleTrack} ${siteThemeEnabled ? styles.themeToggleOn : ''}`}
                      >
                        <span className={styles.themeToggleThumb} />
                      </span>
                      <span className={styles.themeToggleLabel}>
                        {siteThemeEnabled ? 'On' : 'Off'}
                      </span>
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    {siteThemeEnabled ? 'Disable' : 'Enable'} site theme
                  </TooltipContent>
                </Tooltip>
                {previousSiteThemeId && previousSiteThemeId !== (appliedSiteThemeId || activeSiteTheme?.id) && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={async () => {
                          const prev = themes.find((t) => t.id === previousSiteThemeId);
                          if (!prev) {
                            // Previous was no theme — revert to defaults
                            try {
                              await clearSiteTheme();
                              setAppliedSiteThemeId(null);
                              setPreviousSiteThemeId(null);
                              toast.success('Undone — reverted to defaults');
                            } catch { toast.error('Undo failed'); }
                            return;
                          }
                          try {
                            const tokens = JSON.parse(prev.tokens) as ThemeTokens;
                            let pairedTokens: ThemeTokens | undefined;
                            const pairedId = (prev as any).pairedThemeId;
                            if (pairedId) {
                              const paired = themes.find((t) => t.id === pairedId);
                              if (paired) try { pairedTokens = JSON.parse(paired.tokens); } catch {}
                            }
                            await applySiteTheme(prev.id, tokens, prev.name, pairedTokens);
                            setAppliedSiteThemeId(prev.id);
                            setPreviousSiteThemeId(null);
                            toast.success(`Undone — restored "${prev.name}"`);
                          } catch { toast.error('Undo failed'); }
                        }}
                        disabled={isSiteThemeApplying}
                      >
                        <Undo2 size={14} />
                        Undo
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Undo last theme change</TooltipContent>
                  </Tooltip>
                )}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={async () => {
                        try {
                          setPreviousSiteThemeId(appliedSiteThemeId || activeSiteTheme?.id || null);
                          await clearSiteTheme();
                          setAppliedSiteThemeId(null);
                          toast.success('Site theme reverted to default');
                        } catch (err) {
                          toast.error('Failed to revert site theme');
                        }
                      }}
                      disabled={isSiteThemeApplying}
                    >
                      <RotateCcw size={14} />
                      Revert Site Theme
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Remove custom site theme and restore defaults</TooltipContent>
                </Tooltip>
              </>
            )}
            <Button variant="ghost" size="icon-sm" onClick={() => refetchThemes()} title="Refresh">
              <RefreshCw size={14} />
            </Button>
          </div>
        </div>
      </div>

      <div className={styles.tabs}>
        <button
          className={`${styles.tab} ${activeTab === 'themes' ? styles.active : ''}`}
          onClick={() => setActiveTab('themes')}
        >
          <Palette size={14} />
          Your themes ({themes.length})
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'generate' ? styles.active : ''}`}
          onClick={() => setActiveTab('generate')}
        >
          <Sparkles size={14} />
          Generate
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'images' ? styles.active : ''}`}
          onClick={() => setActiveTab('images')}
        >
          <Image size={14} />
          Images ({images.length})
        </button>
      </div>

      <div className={styles.content}>
        {activeTab === 'themes' && (
          <>
            {themesError ? (
              <div className={styles.emptyState}>
                <Palette size={32} className={styles.emptyIcon} />
                <p className={styles.emptyText}>Couldn&apos;t load your themes</p>
                <p className={styles.emptySubtext}>
                  {themesErrorMessage?.message || 'Something went wrong.'} Check you&apos;re signed
                  in and try again.
                </p>
                <Button variant="outline" onClick={() => refetchThemes()}>
                  <RefreshCw size={14} />
                  Retry
                </Button>
              </div>
            ) : themes.length === 0 ? (
              <div className={styles.emptyState}>
                <Palette size={32} className={styles.emptyIcon} />
                <p className={styles.emptyText}>Your themes</p>
                <p className={styles.emptySubtext}>
                  Themes you save with &quot;Save as global&quot; appear here and apply across all
                  sessions. Generate one below.
                </p>
                <Button variant="outline" onClick={() => setActiveTab('generate')}>
                  <Sparkles size={14} />
                  Generate Theme
                </Button>
              </div>
            ) : (
              <div className={styles.themesList}>
                {themes.map((theme) => {
                  const tokens = parseTokens(theme.tokens);
                  const pairedId = (theme as any).pairedThemeId;
                  const pairedTheme = pairedId ? themes.find((t) => t.id === pairedId) : null;
                  const darkTokens = pairedTheme ? parseTokens(pairedTheme.tokens) : null;
                  const hasPair = !!pairedTheme;
                  const ThemeIcon = getThemeIcon(theme.name, theme.description);
                  return (
                    <div
                      key={theme.id}
                      className={`${styles.themeCard} ${theme.isActive ? styles.active : ''}`}
                    >
                      <div className={styles.themeHeader}>
                        <div className={styles.themeInfo}>
                          <h4 className={styles.themeName}>
                            <ThemeIcon
                              size={15}
                              style={{ marginRight: 6, verticalAlign: 'text-bottom' }}
                            />
                            {theme.name}
                            {theme.isActive && (
                              <Badge variant="secondary" style={{ marginLeft: 8 }}>
                                Active
                              </Badge>
                            )}
                            {!hideProjectScope && appliedProjectThemeId === theme.id && (
                              <Badge
                                variant="secondary"
                                style={{
                                  marginLeft: 4,
                                  background: '#22c55e22',
                                  color: '#22c55e',
                                  borderColor: '#22c55e44',
                                }}
                              >
                                Project
                              </Badge>
                            )}
                            {activeSiteTheme?.id === theme.id && (
                              <Badge variant="default" style={{ marginLeft: 4 }}>
                                Site Theme
                              </Badge>
                            )}
                            {hasPair && (
                              <Badge variant="outline" style={{ marginLeft: 4 }}>
                                Light + Dark
                              </Badge>
                            )}
                          </h4>
                          {theme.description && (
                            <p className={styles.themeDescription}>{theme.description}</p>
                          )}
                        </div>
                        <div className={styles.themeActions}>
                          {!theme.isActive && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon-sm"
                                  onClick={() => handleActivateTheme(theme)}
                                >
                                  <Check size={14} />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Activate theme</TooltipContent>
                            </Tooltip>
                          )}
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon-sm"
                                onClick={() => setDeleteConfirm(theme)}
                              >
                                <Trash2 size={14} />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Delete theme</TooltipContent>
                          </Tooltip>
                        </div>
                      </div>
                      {hasPair ? (
                        <>
                          {renderColorPreview(tokens, 'Light', theme)}
                          {renderColorPreview(darkTokens, 'Dark', pairedTheme!)}
                        </>
                      ) : (
                        renderColorPreview(tokens, undefined, theme)
                      )}
                      {theme.imageUrl && (
                        <img src={theme.imageUrl} alt={theme.name} className={styles.themeImage} />
                      )}

                      {/* Typography controls per theme */}
                      {tokens?.typography && (
                        <div className={styles.typographySection}>
                          <div className={styles.typographyGrid}>
                            <div className={styles.typographyField}>
                              <label className={styles.typographyLabel}>Font Family</label>
                              <select
                                className={styles.typographySelect}
                                value={tokens.typography.fontFamily}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  const font = FONT_OPTIONS.find((f) => f.value === val);
                                  if (font) loadGoogleFont(font.label);
                                  handleSavedThemeTypographyChange(theme, 'fontFamily', val);
                                }}
                                style={{ fontFamily: tokens.typography.fontFamily }}
                              >
                                {['Sans-serif', 'Serif', 'Monospace', 'Script'].map((cat) => {
                                  const fonts = FONT_OPTIONS.filter((f) => f.category === cat);
                                  return fonts.length > 0 ? (
                                    <optgroup key={cat} label={cat}>
                                      {fonts.map((f) => (
                                        <option key={f.value} value={f.value} style={{ fontFamily: f.value }}>{f.label}</option>
                                      ))}
                                    </optgroup>
                                  ) : null;
                                })}
                              </select>
                            </div>
                            <div className={styles.typographyField}>
                              <label className={styles.typographyLabel}>Font Size</label>
                              <select
                                className={styles.typographySelect}
                                value={tokens.typography.baseFontSize}
                                onChange={(e) => handleSavedThemeTypographyChange(theme, 'baseFontSize', e.target.value)}
                              >
                                <option value="12px">12px</option>
                                <option value="13px">13px</option>
                                <option value="14px">14px</option>
                                <option value="15px">15px</option>
                                <option value="16px">16px</option>
                                <option value="17px">17px</option>
                                <option value="18px">18px</option>
                                <option value="20px">20px</option>
                              </select>
                            </div>
                            <div className={styles.typographyField}>
                              <label className={styles.typographyLabel}>Font Weight</label>
                              <select
                                className={styles.typographySelect}
                                value={tokens.typography.fontWeight || '400'}
                                onChange={(e) => handleSavedThemeTypographyChange(theme, 'fontWeight', e.target.value)}
                              >
                                <option value="100">Thin</option>
                                <option value="200">Extra Light</option>
                                <option value="300">Light</option>
                                <option value="400">Regular</option>
                                <option value="500">Medium</option>
                                <option value="600">Semi Bold</option>
                                <option value="700">Bold</option>
                                <option value="800">Extra Bold</option>
                                <option value="900">Black</option>
                              </select>
                            </div>
                            <div className={styles.typographyField}>
                              <label className={styles.typographyLabel}>Font Style</label>
                              <select
                                className={styles.typographySelect}
                                value={tokens.typography.fontStyle || 'normal'}
                                onChange={(e) => handleSavedThemeTypographyChange(theme, 'fontStyle', e.target.value)}
                              >
                                <option value="normal">Normal</option>
                                <option value="italic">Italic</option>
                              </select>
                            </div>
                          </div>
                          <p className={styles.typographyPreviewText} style={{ fontFamily: tokens.typography.fontFamily, fontSize: tokens.typography.baseFontSize, fontWeight: Number(tokens.typography.fontWeight || '400'), fontStyle: (tokens.typography.fontStyle || 'normal') as any }}>
                            The quick brown fox jumps over the lazy dog
                          </p>
                        </div>
                      )}

                      {/* Apply Theme Buttons */}
                      <div className={styles.themeFooter}>
                        <div className={styles.applyButtons}>
                          {!hideProjectScope && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() =>
                                    handleApplyThemeToProject(theme, theme.imageUrl ?? undefined)
                                  }
                                  disabled={isApplyingTheme}
                                  className={styles.applyButton}
                                >
                                  {isApplyingTheme ? (
                                    <Loader2 size={14} className={styles.spinnerIcon} />
                                  ) : appliedProjectThemeId === theme.id ? (
                                    <CheckCircle2 size={14} />
                                  ) : (
                                    <FileCode size={14} />
                                  )}
                                  Project
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                Writes CSS theme to this project session only
                              </TooltipContent>
                            </Tooltip>
                          )}
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleApplyThemeToSite(theme)}
                                disabled={isSiteThemeApplying}
                                className={styles.applyButton}
                              >
                                {isSiteThemeApplying ? (
                                  <Loader2 size={14} className={styles.spinnerIcon} />
                                ) : appliedSiteThemeId === theme.id ||
                                  activeSiteTheme?.id === theme.id ? (
                                  <CheckCircle2 size={14} />
                                ) : (
                                  <Monitor size={14} />
                                )}
                                Portal
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              Applies theme across all of your sessions
                            </TooltipContent>
                          </Tooltip>
                        </div>
                        {!hideProjectScope && appliedProjectThemeId === theme.id && appliedProjectFile && (
                          <span className={styles.appliedHint}>
                            Applied to {appliedProjectFile}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

          </>
        )}

        {activeTab === 'generate' && (
          <div className={styles.generatorSection}>
            <div className={styles.generatorForm}>
              <label htmlFor="theme-prompt">Describe your theme</label>
              <Textarea
                id="theme-prompt"
                className={styles.promptInput}
                placeholder="Describe mood, colors, and style..."
                value={generatePrompt}
                onChange={(e) => setGeneratePrompt(e.target.value)}
                rows={3}
              />

              {/* Quick theme suggestions + dice shuffle */}
              <div className={styles.themeSuggestions}>
                {visibleSuggestions.map((suggestion) => (
                  <button
                    key={suggestion.label}
                    type="button"
                    className={`${styles.themeSuggestionChip} ${isDiceRolling ? styles.chipFadeOut : styles.chipFadeIn}`}
                    onClick={() => setGeneratePrompt(suggestion.prompt)}
                  >
                    {suggestion.label}
                  </button>
                ))}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      className={`${styles.diceButton} ${isDiceRolling ? styles.diceRolling : ''}`}
                      onClick={handleDiceRoll}
                      disabled={isDiceRolling}
                      aria-label="Shuffle theme suggestions"
                    >
                      <Dices size={20} />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>Shuffle themes</TooltipContent>
                </Tooltip>
              </div>

              <div className={styles.checkboxRow}>
                <Checkbox
                  id="auto-generate-image"
                  checked={autoGenerateImage}
                  onChange={(e) => setAutoGenerateImage(e.target.checked)}
                />
                <label htmlFor="auto-generate-image" className={styles.checkboxLabel}>
                  <ImagePlus size={14} />
                  Auto-generate matching AI image
                </label>
              </div>

              <div className={styles.providerRow}>
                <span className={styles.providerLabel}>Image Provider</span>
                <div className={styles.providerToggle}>
                  <button
                    type="button"
                    className={`${styles.providerOption} ${imageProvider === 'modelslab' ? styles.providerActive : ''}`}
                    onClick={() => setImageProvider('modelslab')}
                  >
                    AI Generate
                  </button>
                  <button
                    type="button"
                    className={`${styles.providerOption} ${imageProvider === 'gemini' ? styles.providerActive : ''}`}
                    onClick={() => setImageProvider('gemini')}
                  >
                    Nano Banana Pro
                  </button>
                  <button
                    type="button"
                    className={`${styles.providerOption} ${imageProvider === 'flux' ? styles.providerActive : ''}`}
                    onClick={() => setImageProvider('flux')}
                  >
                    Flux
                  </button>
                </div>
              </div>

              <div className={styles.generatorActions}>
                <Button
                  onClick={handleGenerateTheme}
                  disabled={
                    generateThemeMutation.isPending || isGeneratingImage || !generatePrompt.trim()
                  }
                >
                  {generateThemeMutation.isPending ? (
                    <>
                      <Spinner size="sm" />
                      Generating theme...
                    </>
                  ) : isGeneratingImage ? (
                    <>
                      <Spinner size="sm" />
                      Generating image...
                    </>
                  ) : (
                    <>
                      <Sparkles size={14} />
                      Generate Theme {autoGenerateImage && '+ Image'}
                    </>
                  )}
                </Button>
              </div>
            </div>

            {renderGeneratedThemePreview()}
          </div>
        )}

        {activeTab === 'images' && (
          <div className={styles.generatorSection}>
            <div className={styles.generatorForm}>
              <label htmlFor="image-prompt">Image prompt</label>
              <Textarea
                id="image-prompt"
                className={styles.promptInput}
                placeholder="E.g., A futuristic cityscape at sunset, neon lights reflecting off glass buildings, cyberpunk aesthetic, digital art style"
                value={imagePrompt}
                onChange={(e) => setImagePrompt(e.target.value)}
                rows={3}
              />
              <div className={styles.providerRow}>
                <span className={styles.providerLabel}>Image Provider</span>
                <div className={styles.providerToggle}>
                  <button
                    type="button"
                    className={`${styles.providerOption} ${imageProvider === 'modelslab' ? styles.providerActive : ''}`}
                    onClick={() => setImageProvider('modelslab')}
                  >
                    AI Generate
                  </button>
                  <button
                    type="button"
                    className={`${styles.providerOption} ${imageProvider === 'gemini' ? styles.providerActive : ''}`}
                    onClick={() => setImageProvider('gemini')}
                  >
                    Nano Banana Pro
                  </button>
                  <button
                    type="button"
                    className={`${styles.providerOption} ${imageProvider === 'flux' ? styles.providerActive : ''}`}
                    onClick={() => setImageProvider('flux')}
                  >
                    Flux
                  </button>
                </div>
              </div>
              <div className={styles.generatorActions}>
                <Button
                  onClick={handleGenerateImage}
                  disabled={generateImageMutation.isPending || !imagePrompt.trim()}
                >
                  {generateImageMutation.isPending ? (
                    <>
                      <Spinner size="sm" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Image size={14} />
                      Generate Image
                    </>
                  )}
                </Button>
              </div>
            </div>

            {imagesLoading ? (
              <div className={styles.loadingState}>
                <Spinner />
                <span>Loading images...</span>
              </div>
            ) : images.length === 0 ? (
              <div className={styles.emptyState}>
                <Image size={32} className={styles.emptyIcon} />
                <p className={styles.emptyText}>No generated images yet</p>
                <p className={styles.emptySubtext}>
                  Use the form above to generate images using AI.
                </p>
              </div>
            ) : (
              <div className={styles.imageGrid}>
                {images.map((image) => {
                  const imageUrl = normalizeImageDataUrl(image.imageUrl);

                  return (
                    <div key={image.id} className={styles.imageCard}>
                      <img src={imageUrl} alt={image.prompt} className={styles.imagePreview} />
                      <div className={styles.imageInfo}>
                        <p className={styles.imagePrompt}>{image.prompt}</p>
                        <div className={styles.imageActions}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon-sm"
                                onClick={() => {
                                  const link = document.createElement('a');
                                  link.href = imageUrl;
                                  link.download = `image-${image.id}.png`;
                                  link.click();
                                }}
                              >
                                <Download size={12} />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Download</TooltipContent>
                          </Tooltip>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon-sm"
                                onClick={() => handleSaveImageToFiles(image)}
                                disabled={savingImageId === image.id}
                              >
                                {savingImageId === image.id ? (
                                  <Loader2 size={12} className={styles.spinnerIcon} />
                                ) : (
                                  <FolderOpen size={12} />
                                )}
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Save to Files</TooltipContent>
                          </Tooltip>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon-sm"
                                onClick={() => {
                                  navigator.clipboard.writeText(image.prompt);
                                  toast.success('Prompt copied');
                                }}
                              >
                                <Copy size={12} />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Copy prompt</TooltipContent>
                          </Tooltip>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Save Theme Dialog */}
      <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save Theme</DialogTitle>
            <DialogDescription>
              Save this theme to your library and optionally apply it.
            </DialogDescription>
          </DialogHeader>
          {generatedTheme && (
            <div
              style={{
                padding: '0.75rem',
                background: 'var(--surface)',
                borderRadius: 'var(--radius-md)',
                marginBottom: '0.5rem',
              }}
            >
              <div style={{ fontWeight: 700, fontSize: '0.875rem', marginBottom: '0.25rem' }}>
                {generatedTheme.name}
              </div>
              <div style={{ fontSize: '0.75rem', color: 'var(--muted-foreground)' }}>
                {generatedTheme.description}
              </div>
              <div
                style={{ display: 'flex', gap: '0.25rem', marginTop: '0.5rem', flexWrap: 'wrap' }}
              >
                {generatedTheme.tokens?.colors &&
                  Object.entries(generatedTheme.tokens.colors)
                    .slice(0, 6)
                    .map(([name, color]) => (
                      <div
                        key={name}
                        style={{
                          width: '2rem',
                          height: '1.5rem',
                          borderRadius: 'var(--radius-sm)',
                          backgroundColor: color as string,
                          border: '1px solid var(--border)',
                        }}
                        title={`${name}: ${color}`}
                      />
                    ))}
              </div>
            </div>
          )}
          <div className={styles.formFields}>
            <div className={styles.formField}>
              <label>Theme Name</label>
              <Input
                value={themeName}
                onChange={(e) => setThemeName(e.target.value)}
                placeholder="My Custom Theme"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowSaveDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveTheme} disabled={createThemeMutation.isPending}>
              {createThemeMutation.isPending ? <Spinner size="sm" /> : 'Save & Apply'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Theme</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{deleteConfirm?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteConfirm(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteTheme}
              disabled={deleteThemeMutation.isPending}
            >
              {deleteThemeMutation.isPending ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
